/**
 * Where an account's follow-up stands, and how a customer pays.
 *
 * Both are worked out every time they are read. The only thing the stored
 * `status` says is whether a person declared the money collected; the calendar
 * cannot undo that. Today / Upcoming / Overdue / Pending are readings of the
 * follow-up date against today, so the date decides and the stored word is
 * never consulted for them. It used to be rewritten on every save, which turned
 * one edit after midnight into a write on every account whose date had crossed.
 */
import type { Customer, PaymentRank } from './customer.js';
import { FollowUpStatus } from './customer.js';
import { overdueAgeing } from './money.js';

export type FollowUpCategory = 'today' | 'future' | 'overdue' | 'no_follow_up' | 'completed';

type FollowUpInput = Pick<Customer, 'status' | 'followUpDate'>;

const atMidnight = (value: Date): Date => {
    const d = new Date(value);
    d.setHours(0, 0, 0, 0);
    return d;
};

export function getFollowUpCategory(item: FollowUpInput, today: Date = new Date()): FollowUpCategory {
    if (item.status === FollowUpStatus.Completed) return 'completed';

    const t = atMidnight(today);

    if (item.followUpDate) {
        const d = new Date(item.followUpDate);
        if (!isNaN(d.getTime())) {
            d.setHours(0, 0, 0, 0);
            if (d.getTime() === t.getTime()) return 'today';
            if (d.getTime() > t.getTime()) return 'future';
            return 'overdue';
        }
    }

    // No usable date: nothing is planned. The stored word is only ever an old
    // reading of a date that no longer exists, so it says nothing the date does not.
    return 'no_follow_up';
}

/**
 * The same reading in the vocabulary the screens, filters and exports use.
 * Derived, never stored.
 */
export function followUpStatusOf(item: FollowUpInput, today: Date = new Date()): FollowUpStatus {
    switch (getFollowUpCategory(item, today)) {
        case 'completed': return FollowUpStatus.Completed;
        case 'today': return FollowUpStatus.Today;
        case 'future': return FollowUpStatus.Upcoming;
        case 'overdue': return FollowUpStatus.Overdue;
        default: return FollowUpStatus.Pending;
    }
}

type RankInput = Parameters<typeof overdueAgeing>[0] & Pick<Customer, 'paymentRank'>;

/**
 * A customer's payment rank.
 *
 * A rank somebody set by hand always wins — including `Bad`, which is the only
 * way an account becomes one, because being a defaulter is a decision about the
 * customer and not something ageing can tell you. Everything else is worked out
 * from what is owed: nothing overdue is Good, anything overdue is Late.
 */
export function getCustomerPaymentRank(customer: RankInput): PaymentRank {
    // Somebody who knows the account has said what it is; that beats any rule.
    if (customer.paymentRank === 'Good' || customer.paymentRank === 'Late' || customer.paymentRank === 'Bad') {
        return customer.paymentRank;
    }

    // Anything past its date is Late, however far past. Old money is shown by
    // the ageing buckets, which say how old far better than a grade can; it is
    // not evidence that a customer has stopped paying. An account in credit
    // owes nothing, and overdueAgeing() says so.
    const { over45, over90 } = overdueAgeing(customer);
    if (over90 > 0 || over45 > 0) return 'Late';

    return 'Good';
}

/**
 * A declared defaulter is not routine work.
 *
 * The worklists, the counts on their cards, the badge on Today, the team table
 * and the morning email all leave these accounts out, and one list — the
 * recovery list — carries them. Left in, they sit in "Overdue" for ever and the
 * people working the day's follow-ups scroll past them every morning; chasing a
 * defaulter is a decision, not a daily reflex. Nothing is hidden: the customer
 * book still shows every account, and the recovery list is one press away.
 */
export const isBadDebt = (item: RankInput): boolean => getCustomerPaymentRank(item) === 'Bad';
